import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { format, differenceInDays, isBefore } from 'date-fns';

interface AccelerateInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (adjustment: any) => void;
}

// Mock invoices
const MOCK_INVOICES = [
  { id: '1234', customer: 'BigCo Ltd', amount: 22000, dueDate: new Date('2025-11-23') },
  { id: '1235', customer: 'SmallCo', amount: 18500, dueDate: new Date('2025-11-30') },
  { id: '1240', customer: 'MedCo Inc', amount: 15200, dueDate: new Date('2025-12-05') },
];

export function AccelerateInvoiceModal({ open, onOpenChange, onAdd }: AccelerateInvoiceModalProps) {
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [newDate, setNewDate] = useState<Date>();
  const [reason, setReason] = useState('');

  const invoice = MOCK_INVOICES.find(inv => inv.id === selectedInvoice);
  const currentDate = invoice?.dueDate;

  const daysDifference = currentDate && newDate ? differenceInDays(currentDate, newDate) : 0;
  const isValid = selectedInvoice && newDate && currentDate && isBefore(newDate, currentDate);

  useEffect(() => {
    if (!open) {
      setSelectedInvoice('');
      setNewDate(undefined);
      setReason('');
    }
  }, [open]);

  const handleAdd = () => {
    if (!isValid || !invoice) return;

    onAdd({
      type: 'accelerate-invoice',
      description: `Accelerate Invoice #${invoice.id} - ${invoice.customer}`,
      amount: invoice.amount,
      originalDate: currentDate,
      newDate: newDate,
      reason,
      impact: `Accelerates £${(invoice.amount / 1000).toFixed(0)}K inflow by ${daysDifference} days`,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Accelerate Invoice Payment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select Invoice */}
          <div className="space-y-2">
            <Label>Select Invoice *</Label>
            <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
              <SelectTrigger>
                <SelectValue placeholder="Search invoices..." />
              </SelectTrigger>
              <SelectContent>
                {MOCK_INVOICES.map((inv) => (
                  <SelectItem key={inv.id} value={inv.id}>
                    Invoice #{inv.id} - {inv.customer} - £{inv.amount.toLocaleString()} (Due {format(inv.dueDate, 'MMM dd')})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current Due Date */}
          {currentDate && (
            <div className="space-y-2">
              <Label>Current Due Date</Label>
              <Input value={format(currentDate, 'MMM dd, yyyy')} disabled />
            </div>
          )}

          {/* New Due Date */}
          <div className="space-y-2">
            <Label>New Due Date *</Label>
            <DatePicker
              value={newDate}
              onChange={setNewDate}
              dateFormat="MMM dd, yyyy"
              placeholder="Pick a date"
              disabledDate={(date) => currentDate ? !isBefore(date, currentDate) : false}
            />
            {currentDate && newDate && !isBefore(newDate, currentDate) && (
              <p className="text-sm text-red-500">New date must be earlier than current due date</p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              placeholder="e.g., Early payment discount negotiated"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          {/* Impact Preview */}
          {isValid && invoice && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-sm font-medium">Impact Preview</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Cash impact on {format(currentDate!, 'MMM dd')}:</span>
                  <span className="text-red-500">-£{invoice.amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cash impact on {format(newDate!, 'MMM dd')}:</span>
                  <span className="text-green-500">+£{invoice.amount.toLocaleString()}</span>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">
                    Net effect: Accelerates £{(invoice.amount / 1000).toFixed(0)}K inflow by {daysDifference} days
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!isValid}>
              Add Adjustment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
